import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store'

// Views
import Home from '@/views/Home'
import Intro from '@/views/Intro'
import Vote from '@/views/Vote'
import Voted from '@/views/Voted'
import Submit from '@/views/Submit'
import SubmitSuccess from '@/views/SubmitSuccess'
import Leaderboard from '@/views/Leaderboard'
import Gallery from '@/views/Gallery'
import Login from '@/views/Login'
import Admin from '@/views/Admin'
import ActiveCampaign from '@/views/ActiveCampaign'
import CreateCampaign from '@/views/CreateCampaign'
import GlobalSettings from '@/views/GlobalSettings'
import VoteModeration from '@/views/VoteModeration'
import ModerateCampaign from '@/views/ModerateCampaign'
import Campaigns from '@/views/Campaigns'
import ViewCampaign from '@/views/ViewCampaign'
import ManageCampaigns from '@/views/ManageCampaigns'
import ArchivedCampaigns from '@/views/ArchivedCampaigns'
import BlockedIPs from '@/views/BlockedIPs'
import EditCampaign from '@/views/EditCampaign'
import AddCampaignSubmission from '@/views/AddCampaignSubmission'
import Accounts from '@/views/Accounts'
import ManageAccounts from '@/views/ManageAccounts'
import CreateAdminAccount from '@/views/CreateAdminAccount'
import EditAdminAccount from '@/views/EditAdminAccount'

import Wall from '@/views/Wall'
import WallVote from '@/views/WallVote'
import WallLeaderboard from '@/views/WallLeaderboard'
import WallGallery from '@/views/WallGallery'

Vue.use(Router)

/**
 * Middlewares
 */

// Loads the active campaign
function loadActiveCampaign(to, from, next) {
	if (store.getters['campaign/notLoaded']) store.dispatch('campaign/load')
	next()
}

// Protects routes that require login
function protectAdmin(to, from, next) {
	if (store.getters['auth/loggedIn']) next()
	else next({ name: 'Login' })
}

// Only allows visiting submit success page from submit page
function redirectSubmitSuccess(to, from, next) {
	if (from.name === 'Submit') next()
	else next({ name: 'Submit' })
}

// Redirect login page to admin console for authenticated users
function redirectLogin(to, from, next) {
	if (!store.getters['auth/loggedIn']) next()
	else next({ name: 'Current Campaign' })
}

// Only allow visiting voting page if a voting session is in progress
function redirectVoting(to, from, next) {
	if (store.getters['session/exists']) next()
	else next({ name: 'Home' })
}

// Only allows visiting voted page from vote page
function redirectVoted(to, from, next) {
	if (from.name === 'Vote') next()
	else next({ name: 'Home' })
}

// Log user out if they visit this route
function logOut(to, from, next) {
	store.dispatch('auth/logOut')
	next({ name: 'Home' })
}

/**
 * Routes
 */

const router = new Router({
	mode: 'history',
	base: process.env.BASE_URL,
	routes: [
		{
			path: '/',
			component: Home,
			beforeEnter: loadActiveCampaign,
			children: [
				{
					path: '',
					name: 'Home',
					component: Intro
				},
				{
					path: 'submit',
					name: 'Submit',
					component: Submit
				},
				{
					path: 'submit/done',
					name: 'Submission successful',
					component: SubmitSuccess,
					beforeEnter: redirectSubmitSuccess
				},
				{
					path: 'voted',
					name: 'Voted',
					component: Voted,
					beforeEnter: redirectVoted
				},
				{
					path: 'leaderboard',
					name: 'Leaderboard',
					component: Leaderboard
				},
				{
					path: 'gallery',
					name: 'Gallery',
					component: Gallery
				}
			]
		},
		{
			path: '/vote',
			name: 'Vote',
			component: Vote,
			beforeEnter: redirectVoting,
			meta: {
				overlay: true
			}
		},
		{
			path: '/wall',
			component: Wall,
			beforeEnter: loadActiveCampaign,
			children: [
				{
					path: 'vote',
					name: 'Wall Vote',
					component: WallVote
				},
				{
					path: 'leaderboard',
					name: 'Wall Leaderboard',
					component: WallLeaderboard
				},
				{
					path: 'gallery',
					name: 'Wall Gallery',
					component: WallGallery
				}
			]
		},
		{
			path: '/login',
			name: 'Login',
			component: Login,
			beforeEnter: redirectLogin
		},
		{
			path: '/admin',
			component: Admin,
			beforeEnter: protectAdmin,
			children: [
				{
					path: 'current',
					name: 'Current Campaign',
					component: ActiveCampaign,
					meta: {
						baseTitle: 'okHiQ Admin'
					}
				},
				{
					path: 'create',
					name: 'Create Campaign',
					component: CreateCampaign,
					meta: {
						baseTitle: 'okHiQ Admin'
					}
				},
				{
					path: 'manage',
					component: Campaigns,
					children: [
						{
							path: '',
							name: 'Manage Campaigns',
							component: ManageCampaigns,
							meta: {
								baseTitle: 'okHiQ Admin'
							}
						},
						{
							path: ':id/view',
							name: 'View Campaign',
							component: ViewCampaign,
							meta: {
								baseTitle: 'okHiQ Admin'
							}
						},
						{
							path: ':id/edit',
							name: 'Edit Campaign',
							component: EditCampaign,
							meta: {
								baseTitle: 'okHiQ Admin'
							}
						},
						{
							path: ':id/add',
							name: 'Add Submission',
							component: AddCampaignSubmission,
							meta: {
								baseTitle: 'okHiQ Admin'
							}
						}
					]
				},
				{
					path: 'archived',
					name: 'Archived Campaigns',
					component: ArchivedCampaigns,
					meta: {
						baseTitle: 'okHiQ Admin'
					}
				},
				{
					path: 'blocked',
					name: 'Blocked IP Addresses',
					component: BlockedIPs,
					meta: {
						baseTitle: 'okHiQ Admin'
					}
				},
				{
					path: 'moderate',
					name: 'Vote Moderation',
					component: VoteModeration,
					meta: {
						baseTitle: 'okHiQ Admin'
					},
					children: [
						{
							path: ':id',
							name: 'Moderate Campaign',
							component: ModerateCampaign,
							meta: {
								baseTitle: 'okHiQ Admin'
							}
						}
					]
				},
				{
					path: 'settings',
					name: 'Global Settings',
					component: GlobalSettings,
					meta: {
						baseTitle: 'okHiQ Admin'
					}
				},
				{
					path: 'accounts',
					component: Accounts,
					children: [
						{
							path: '',
							name: 'Admin Accounts',
							component: ManageAccounts,
							meta: {
								baseTitle: 'okHiQ Admin'
							}
						},
						{
							path: 'add',
							name: 'Create Admin Account',
							component: CreateAdminAccount,
							meta: {
								baseTitle: 'okHiQ Admin'
							}
						},
						{
							path: ':id',
							name: 'Edit Admin Account',
							component: EditAdminAccount,
							meta: {
								baseTitle: 'okHiQ Admin'
							}
						}
					]
				}
			]
		},
		{
			path: '/logout',
			name: 'Log Out',
			beforeEnter: logOut
		}
	]
})

/**
 * Global navigation guards
 */

// On admin/login routes, check once to see if we're logged in
let authCheck = false
const authCheckPages = ['/login', '/admin']
async function doAuthCheck(to, from, next) {
	if (!authCheck && authCheckPages.some(path => to.path.startsWith(path))) {
		authCheck = true
		await store.dispatch('auth/check')
	}
	next()
}

// Force admin root to current campaign page
function forceAdminDefaultPage(to, from, next) {
	if (to.path !== '/admin') next()
	else next({ name: 'Current Campaign' })
}

router.beforeEach(doAuthCheck)
router.beforeEach(forceAdminDefaultPage)

export default router
